import React from 'react';

import useMutateAppointmentCancellation from '../../hooks/useMutateAppointmentCancellation';
import { ACTION_STATUS } from '../../AppointmentItemActionButtons';

import CancelButton from './components/CancelButton';

export interface AppointmentCancelButtonProps {
  disabled: boolean;
  appointmentId: string;
  setActionsStatus: React.Dispatch<React.SetStateAction<ACTION_STATUS>>;
}

enum STATUS {
  Initial = 'initial',
  Confirmation = 'confirmation',
  Pending = 'pending',
  Resolved = 'resolved',
}

const AppointmentCancelButton = (props: AppointmentCancelButtonProps) => {
  const { disabled, appointmentId, setActionsStatus } = props;
  const [status, setStatus] = React.useState<STATUS>(STATUS.Initial);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);

  const {
    mutate: mutateAppointmentCancellation,
    isLoading: isLoadingCancellation,
  } = useMutateAppointmentCancellation();

  React.useEffect(() => {
    const resetStateOnClickAway = (event: any) => {
      if (buttonRef.current && status === 'confirmation' && !buttonRef.current.contains(event.target)) {
        setStatus(STATUS.Initial);
      }
    };

    document.addEventListener('mousedown', resetStateOnClickAway);

    return () => {
      document.removeEventListener('mousedown', resetStateOnClickAway);
    };
  }, [status]);

  const handleAppointmentCancellation = React.useCallback(() => {
    setStatus(STATUS.Pending);
    setActionsStatus(ACTION_STATUS.Canceled);
    mutateAppointmentCancellation(appointmentId, {
      onSuccess: () => setStatus(STATUS.Resolved),
      onError: () => {
        setStatus(STATUS.Initial);
        setActionsStatus(ACTION_STATUS.Initial);
      },
    });
  }, [appointmentId, mutateAppointmentCancellation, setActionsStatus]);

  const isLoading = isLoadingCancellation || status === STATUS.Pending;

  if (isLoading) {
    return <CancelButton.Loading />;
  }

  if (status === STATUS.Initial) {
    return <CancelButton.Initial disabled={disabled} onClick={() => setStatus(STATUS.Confirmation)} />;
  }

  if (status === STATUS.Confirmation) {
    return <CancelButton.Confirmation buttonRef={buttonRef} onClick={handleAppointmentCancellation} />;
  }

  if (status === STATUS.Resolved) {
    return <CancelButton.Canceled />;
  }

  return null;
};

export default AppointmentCancelButton;
